import { trapFocus } from "@zag-js/focus-trap";

const TRAP_OPTIONS = { returnFocusOnDeactivate: false, getShadowRoot: true };

/**
 * Lets a layer outside the open dialog take focus, such as a third-party modal portalled to
 * `<body>`: traps the roots present now and each root mounted later, stacked over the dialog's
 * trap. Returns the release.
 */
export function yieldDialogFocus(resolveRoots: () => Element[]): () => void {
  // Map order is activation order: releasing front to back never unpauses a removed trap.
  const traps = new Map<HTMLElement, () => void>();

  const releaseWhere = (shouldRelease: (root: HTMLElement) => boolean) => {
    for (const [root, release] of traps) {
      if (!shouldRelease(root)) continue;
      traps.delete(root);
      release();
    }
  };

  // A nested root is covered by its ancestor's trap; its own trap would pause that one.
  const isCovered = (root: HTMLElement, roots: HTMLElement[]) =>
    roots.some((other) => other !== root && other.contains(root)) ||
    [...traps.keys()].some((trapped) => trapped.contains(root));

  const sync = () => {
    releaseWhere((root) => !root.isConnected);
    const roots = resolveRoots().filter((el): el is HTMLElement => el instanceof HTMLElement);
    for (const root of roots) {
      if (traps.has(root) || isCovered(root, roots)) continue;
      traps.set(root, trapFocus([root], TRAP_OPTIONS));
    }
  };

  sync();
  const observer = new MutationObserver(sync);
  observer.observe(document.body, { childList: true, subtree: true });
  return () => {
    observer.disconnect();
    releaseWhere(() => true);
  };
}
